import React, { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, FileWarning } from "lucide-react";

const ACCEPT = ".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.md,.png,.jpg,.jpeg,.webp,.gif,.heic,.heif,.dwg,.dxf,.zip";

/* Dir 147 — ONE-CLICK RECOVERY dropzone. Shown only when a pursuit is blocked on NO SOURCE DOCUMENTS. Drop the
   solicitation package in; each file is uploaded (which auto-shreds), and the backend auto-resumes Anvil once
   the source block clears. Turns a docless dead-end into a ~10-second recovery. */
export function SourceDocGate({ dealId, onDone }) {
  const inputRef = useRef(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [url, setUrl] = useState("");
  const [fetching, setFetching] = useState(false);

  const uploadAll = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    setBusy(true);
    let resumed = false;
    try {
      for (let i = 0; i < list.length; i++) {
        setProgress(`Uploading ${i + 1}/${list.length} — ${list[i].name}`);
        const fd = new FormData();
        fd.append("file", list[i]);
        const r = await api.post(`/bids/${dealId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (r?.data?.resumed) resumed = true;
      }
      toast.success(
        resumed
          ? "Package received — source block cleared. Anvil is auto-resuming."
          : `${list.length} file(s) uploaded.`
      );
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed.");
    } finally {
      setBusy(false);
      setProgress("");
    }
  };

  const fetchUrl = async () => {
    const u = url.trim();
    if (!u) { toast.error("Paste a public solicitation URL first."); return; }
    setFetching(true);
    try {
      const r = await api.post(`/bids/${dealId}/documents/fetch-url`, { url: u });
      toast.success(
        r?.data?.resumed
          ? "Document fetched — source block cleared. Anvil is auto-resuming."
          : `Fetched ${r?.data?.original_filename || "document"}.`
      );
      setUrl("");
      onDone?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch that URL.");
    } finally {
      setFetching(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (!busy) uploadAll(e.dataTransfer?.files);
  };

  return (
    <div
      className="mb-5 rounded-xl border-2 border-amber-300 bg-amber-50/70 p-5"
      data-testid="source-doc-gate"
    >
      <div className="flex items-start gap-3">
        <FileWarning className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0">
          <div className="text-sm font-semibold text-amber-900">
            No solicitation documents — Anvil is blocked from pricing
          </div>
          <div className="text-[12px] text-amber-800 mt-0.5">
            SAM.gov has the notice but posted no attachments (and none were on HigherGov), so there is nothing to
            ground against. Drop the solicitation package (SOW, drawings, Section L/M, any pricing/rate forms)
            below — Anvil will ingest it and resume automatically.
          </div>
        </div>
      </div>

      <div
        role="button"
        tabIndex={0}
        onClick={() => !busy && inputRef.current?.click()}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && !busy && inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={onDrop}
        data-testid="source-doc-dropzone"
        className={`mt-4 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed
          px-6 py-8 text-center cursor-pointer transition-colors ${
            drag ? "border-amber-500 bg-amber-100" : "border-amber-300 bg-card/70 hover:bg-amber-100/60"
          } ${busy ? "opacity-70 pointer-events-none" : ""}`}
      >
        {busy ? (
          <>
            <Loader2 className="h-6 w-6 text-amber-600 animate-spin" />
            <div className="text-[12px] text-amber-800 font-medium" data-testid="source-doc-progress">
              {progress || "Uploading…"}
            </div>
          </>
        ) : (
          <>
            <UploadCloud className="h-7 w-7 text-amber-600" />
            <div className="text-[13px] font-medium text-amber-900">
              Drag &amp; drop the package here, or click to browse
            </div>
            <div className="text-[11px] text-amber-700">PDF, Office, images, DWG/DXF — multiple files OK</div>
          </>
        )}
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          data-testid="source-doc-file-input"
          onChange={(e) => uploadAll(e.target.files)}
        />
      </div>

      <div className="mt-3">
        <div className="text-[11px] text-amber-800 mb-1">
          …or paste a <span className="font-medium">public</span> solicitation URL (SAM.gov, DLA DIBBS, and other
          public procurement domains only). Authenticated portals: download the files and drop them above.
        </div>
        <div className="flex gap-2">
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && !fetching && fetchUrl()}
            placeholder="https://sam.gov/…  or  https://dibbs.bsm.dla.mil/…"
            data-testid="source-doc-url-input"
            disabled={fetching}
            className="flex-1 text-[12px] border border-amber-300 rounded px-2 py-1.5 bg-card/80 disabled:opacity-60"
          />
          <button
            onClick={fetchUrl}
            disabled={fetching}
            data-testid="source-doc-url-fetch"
            className="text-[12px] px-3 py-1.5 rounded bg-amber-600 text-white font-medium hover:bg-amber-700 disabled:opacity-60 whitespace-nowrap"
          >
            {fetching ? "Fetching…" : "Fetch"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default SourceDocGate;
